import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Box, Flex, Text, Button, LucideIcon } from '@skin-seoul/design-system';
import { ReviewWriteForm } from './ReviewWriteForm';
import { getDefaultConfirmationData } from '@skin-seoul/service/src/utils/review/modalUtils';
import { CompleteView } from './common/CompleteView';
import { ConfirmationDialog } from './common/ConfirmationDialog';
import { COLORS, SPACING, Z_INDEX } from './common/constants';
import { useReviewForm } from '@skin-seoul/service/src/hooks/review/useReviewForm';
import { useReviewModalState } from '@skin-seoul/service/src/hooks/review/useReviewModalState';
import { useReviewSubmission } from '@skin-seoul/service/src/hooks/review/useReviewSubmission';
import type { ReviewModalProps } from '@skin-seoul/service';

const MotionBox = motion.create(Box);

export const ReviewWritePcModal = ({ isOpen, productInfo, onClose, onSubmit }: ReviewModalProps) => {
  const { formState, formActions, formOptions, hasUnsavedChanges, isSubmitDisabled, resetForm } = useReviewForm({
    isOpen,
  });

  const { isComplete, showConfirmation, setIsComplete, handleClose, handleConfirmClose, handleCancelClose } =
    useReviewModalState({
      isOpen,
      onClose: () => {
        resetForm();
        onClose();
      },
      hasUnsavedChanges,
      enableConfirmation: true,
    });

  const { handleSubmit } = useReviewSubmission({
    formData: formState,
    onSubmit,
    onSubmissionComplete: () => setIsComplete(true),
  });

  const confirmationData = getDefaultConfirmationData();

  return (
    <>
      <AnimatePresence mode="wait">
        {isOpen && (
          <MotionBox
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(0, 0, 0, 0.5)"
            zIndex={Z_INDEX.modal}
            display="flex"
            alignItems="center"
            justifyContent="center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            onClick={handleClose}
          >
            <MotionBox
              bg={COLORS.background}
              borderRadius="24px"
              maxW="638px"
              w="90vw"
              maxH="90dvh"
              boxShadow={`0 8px 32px ${COLORS.shadow}`}
              display="flex"
              flexDirection="column"
              overflow="hidden"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: 'spring',
                damping: 20,
                stiffness: 300,
                duration: 0.3,
              }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {!isComplete && (
                <Flex align="center" justify="space-between" p={`${SPACING.xl} ${SPACING.xl} 0px ${SPACING.xl}`}>
                  <Text textStyle="paragraph.large" fontWeight="semibold">
                    Leave a Review
                  </Text>
                  <Button bg="none" border="none" onClick={handleClose}>
                    <LucideIcon name="X" w="24px" h="24px" color="generic.100" />
                  </Button>
                </Flex>
              )}
              <Box flex="1" overflowY="auto" p={`${SPACING.lg} ${SPACING.xl} ${SPACING.xl} ${SPACING.xl}`}>
                {!isComplete ? (
                  <ReviewWriteForm
                    formData={formState}
                    formActions={formActions}
                    options={formOptions}
                    productInfo={productInfo}
                    isSubmitDisabled={isSubmitDisabled}
                    onSubmit={handleSubmit}
                    variant="pc"
                  />
                ) : (
                  <CompleteView onContinue={onClose} variant="pc" />
                )}
              </Box>
            </MotionBox>
          </MotionBox>
        )}
      </AnimatePresence>

      <ConfirmationDialog
        isOpen={showConfirmation}
        title={confirmationData.title}
        message={confirmationData.message}
        confirmText={confirmationData.confirmText}
        cancelText={confirmationData.cancelText}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
};
